/**
 * CZNA-628 — the frontend half of the substring-search floor.
 *
 * The rule itself lives in the backend
 * (`SearchQueryGuard.java`, which owns the measurements and returns 400): a `CONTAINS` search with
 * no three-character run of letters or digits cannot use the trigram index and costs a full pass
 * over 2.25M headings — 13.7 s for `a`, 7.1 s for `no` on the local corpus, both answering with a
 * capped 1000 that tells the cataloguer nothing.
 *
 * This copy exists so the list never fires such a request in the first place: the user gets a hint
 * instead of a five-second wait followed by a red error. It is deliberately the same predicate, not
 * an approximation of it — `authorities.search.tooShort` is what the user reads, and it would be
 * wrong the moment the two disagreed. `AuthoritiesList` renders that hint;
 * `RecordControllerTest.ShortQueryCeiling` is what stops the backend half from drifting away.
 *
 * Prefix search (`searchMode=STARTS_WITH`, both record pickers) has a LOWER floor on the backend,
 * not an exemption — one letter is enough to anchor on (492 ms at one character), but a prefix with
 * no word content at all is the same full scan and is refused there too (`_` alone measured
 * 10.3 s). This module deliberately covers only the substring floor, because the authority list is
 * the only `CONTAINS` caller; the pickers always type letters, so they never approach either floor.
 * Do not reuse `isQueryTooShort` for a prefix search — it would apply the wrong threshold.
 */
export var MIN_QUERY_WORD_RUN = 3;
/**
 * Longest unbroken run of letters or digits. Spaces, punctuation and LIKE metacharacters break the
 * run, which is why `a b`, `, j` and `___` are as unusable to the index as `no` is.
 *
 * `Array.from` so a non-BMP character counts once rather than as its two UTF-16 units.
 */
/**
 * Letters plus decimal digits — `Character.isLetterOrDigit`'s class, which the backend half uses.
 * `\p{Nd}`, not `\p{N}`: the latter also admits No/Nl (½, ², Ⅷ), and this half being the more
 * permissive of the two is exactly how a query gets past the hint and into a raw 400.
 *
 * **Built with `new RegExp` rather than written as a literal, and that is load-bearing.** Unicode
 * property escapes require the `u` flag, and `stripes build` type-checks this file at a target
 * below ES6, where a `u`-flagged literal is a hard error (TS1501) — while the module's own
 * `tsconfig.json` says ES2020, so `tsc --noEmit` and `jest` both pass and only the platform build
 * fails. A regex compiled from a string is not parsed by the type-checker, and the flag itself is
 * fine at runtime: property escapes are ES2018, supported by every browser Stripes targets. Do not
 * "tidy" this back into a literal.
 */
var WORD_CHAR = new RegExp('[\\p{L}\\p{Nd}]', 'u');
export var longestWordRun = function (query) {
    var longest = 0;
    var current = 0;
    // NFC first, for the same reason the Java half does it: in NFD a Czech surname's combining
    // marks are category Mn and would break the run, refusing five letters as if they were two.
    Array.from(query.normalize('NFC')).forEach(function (ch) {
        if (WORD_CHAR.test(ch)) {
            current += 1;
            if (current > longest)
                longest = current;
        }
        else {
            current = 0;
        }
    });
    return longest;
};
/**
 * True when a non-empty substring query is too thin for the heading index — i.e. when the list
 * should show a hint rather than issue the request. An empty query is a filter-only listing and is
 * never too short.
 */
export var isQueryTooShort = function (query) {
    if (!query || !query.trim())
        return false;
    return longestWordRun(query) < MIN_QUERY_WORD_RUN;
};
